import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import useUpdateShopPreferences from "@babackapp/private-api-client/client/use-update-shop-preferences";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Form, FormField } from "@babackapp/ui/components/form";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { OverrideRulesManager } from "../override-rules";

const LogisticianOptionsFormValues = z.object({
  autoTreateOnLogisticianValidate: z.boolean().default(false),
  showHomeToLogistician: z.boolean().default(true),
  showReturnRateToLogistician: z.boolean().default(true),
});

export type LogisticianOptionsFormValuesType = z.infer<
  typeof LogisticianOptionsFormValues
>;

export function LogisticianOptionsForm() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateShopPreference } = useUpdateShopPreferences(
    shop.id,
  );

  const form = useForm<LogisticianOptionsFormValuesType>({
    defaultValues: {
      autoTreateOnLogisticianValidate:
        shop.preferences.autoTreateOnLogisticianValidate || false,
      showHomeToLogistician: shop.preferences.showHomeToLogistician !== false,
      showReturnRateToLogistician:
        shop.preferences.showReturnRateToLogistician !== false,
    },
  });

  const values = useWatch({ control: form.control });

  const onSubmit = form.handleSubmit(
    async ({
      autoTreateOnLogisticianValidate,
      showHomeToLogistician,
      showReturnRateToLogistician,
    }) => {
      updateShopPreference({
        autoTreateOnLogisticianValidate,
        showHomeToLogistician,
        showReturnRateToLogistician,
      }).then(() =>
        form.reset({
          autoTreateOnLogisticianValidate,
          showHomeToLogistician,
          showReturnRateToLogistician,
        }),
      );
    },
  );

  useEffect(() => {
    if (form.formState.isDirty) onSubmit();
  }, [values, form.formState.isDirty]);

  const planActivated = true || !!shop.plan.workflowLogistician;
  const isAdmin = useViewerHasAtLeastAdminRights();

  const disabledBecausePlanNotActivated = planActivated
    ? false
    : t("settings.shop.errors.planNotActivated");

  const disabledBecauseNotAdmin = isAdmin
    ? false
    : t("settings.shop.errors.notAdmin");

  const workflowActivated = shop.workflowLogistician.activated;

  const switchIsDisabled =
    disabledBecauseNotAdmin || disabledBecausePlanNotActivated;

  const displaySwitchIsDisabled = disabledBecauseNotAdmin;

  if (!workflowActivated) return null;

  return (
    <div>
      <Form {...form}>
        <form onSubmit={onSubmit}>
          <FormField
            control={form.control}
            name="autoTreateOnLogisticianValidate"
            render={({ field }) => (
              <FormSwitch
                label={t(
                  "settings.customerPortal.basics.fields.autoTreateOnLogisticianValidate.title",
                )}
                description={t(
                  "settings.customerPortal.basics.fields.autoTreateOnLogisticianValidate.description",
                )}
                {...field}
                disabled={switchIsDisabled}
              />
            )}
          />
        </form>
      </Form>
      <OverrideRulesManager actionType={"OVERRIDE_AUTO_TREATMENT"} />
      <hr className="my-6" />
      <Form {...form}>
        <form onSubmit={onSubmit}>
          <FormField
            control={form.control}
            name="showHomeToLogistician"
            render={({ field }) => (
              <FormSwitch
                label={t(
                  "settings.customerPortal.basics.fields.showHomeToLogistician.title",
                )}
                description={t(
                  "settings.customerPortal.basics.fields.showHomeToLogistician.description",
                )}
                {...field}
                disabled={displaySwitchIsDisabled}
              />
            )}
          />
          <FormField
            control={form.control}
            name="showReturnRateToLogistician"
            render={({ field }) => (
              <FormSwitch
                label={t(
                  "settings.customerPortal.basics.fields.showReturnRateToLogistician.title",
                )}
                description={t(
                  "settings.customerPortal.basics.fields.showReturnRateToLogistician.description",
                )}
                {...field}
                disabled={displaySwitchIsDisabled}
              />
            )}
          />
        </form>
      </Form>
    </div>
  );
}
